import { useRef, useEffect } from 'react';
import type { DataTableColumn, FilterValue, DiscoveredValue } from '../types';
import { TextFilter } from './TextFilter';
import { NumberFilter } from './NumberFilter';
import { DateFilter } from './DateFilter';
import { EnumFilter } from './EnumFilter';
import { BooleanFilter } from './BooleanFilter';

interface FilterPopoverProps<T> {
  column: DataTableColumn<T>;
  value: FilterValue | null;
  discoveredValues?: DiscoveredValue[];
  onChange: (value: FilterValue | null) => void;
  onClose: () => void;
}

export function FilterPopover<T>({
  column,
  value,
  discoveredValues,
  onChange,
  onClose,
}: FilterPopoverProps<T>) {
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        onClose();
      }
    }
    // Delay to prevent immediate close from the button click
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Close on escape
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const renderFilter = () => {
    switch (column.type) {
      case 'text':
        return (
          <TextFilter
            value={value?.type === 'text' ? value.value : ''}
            onChange={(v) => onChange(v ? { type: 'text', value: v } : null)}
          />
        );

      case 'number':
      case 'currency':
        return (
          <NumberFilter
            min={value?.type === 'number' ? value.min : undefined}
            max={value?.type === 'number' ? value.max : undefined}
            onChange={(min, max) =>
              min !== undefined || max !== undefined
                ? onChange({ type: 'number', min, max })
                : onChange(null)
            }
            prefix={column.numberFormat?.prefix}
          />
        );

      case 'date':
      case 'datetime':
        return (
          <DateFilter
            from={value?.type === 'date' ? value.from : undefined}
            to={value?.type === 'date' ? value.to : undefined}
            onChange={(from, to) =>
              from || to ? onChange({ type: 'date', from, to }) : onChange(null)
            }
          />
        );

      case 'enum':
        return (
          <EnumFilter
            options={column.enumOptions ?? []}
            selected={value?.type === 'enum' ? value.selected : []}
            onChange={(selected) =>
              selected.length > 0 ? onChange({ type: 'enum', selected }) : onChange(null)
            }
          />
        );

      case 'dynamic-enum':
        return (
          <EnumFilter
            options={(discoveredValues ?? []).map((dv) => ({
              value: dv.value,
              label: `${dv.label} (${dv.count})`,
            }))}
            selected={value?.type === 'enum' ? value.selected : []}
            onChange={(selected) =>
              selected.length > 0 ? onChange({ type: 'enum', selected }) : onChange(null)
            }
          />
        );

      case 'multi-enum':
      case 'multi-text':
        return (
          <EnumFilter
            options={(discoveredValues ?? []).map((dv) => ({
              value: dv.value,
              label: `${dv.label} (${dv.count})`,
            }))}
            selected={value?.type === 'multi-enum' ? value.selected : []}
            mode={value?.type === 'multi-enum' ? value.mode : 'include'}
            showModeToggle
            onChange={(selected, mode) =>
              selected.length > 0
                ? onChange({ type: 'multi-enum', selected, mode: mode ?? 'include' })
                : onChange(null)
            }
          />
        );

      case 'boolean':
        return (
          <BooleanFilter
            value={value?.type === 'boolean' ? value.value : null}
            onChange={(v) => onChange(v !== null ? { type: 'boolean', value: v } : null)}
          />
        );

      default:
        return (
          <TextFilter
            value={value?.type === 'text' ? value.value : ''}
            onChange={(v) => onChange(v ? { type: 'text', value: v } : null)}
          />
        );
    }
  };

  return (
    <div
      ref={popoverRef}
      className="absolute left-0 top-full mt-1 bg-white rounded-xl shadow-lg border border-sage-200 p-3 z-50 min-w-[200px]"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="text-xs font-medium text-sage-500 uppercase tracking-wider mb-2">
        Filter: {column.label}
      </div>
      {renderFilter()}
      <div className="flex justify-end gap-2 mt-3 pt-2 border-t border-sage-100">
        <button
          onClick={() => onChange(null)}
          className="px-2 py-1 text-sm text-sage-600 hover:text-sage-800"
        >
          Clear
        </button>
        <button
          onClick={onClose}
          className="px-3 py-1 text-sm bg-forest-700 text-white rounded-lg hover:bg-forest-800"
        >
          Apply
        </button>
      </div>
    </div>
  );
}
